import React, { useState } from 'react';
import { Box, Button, Container, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import { keyframes } from '@emotion/react';
import Logo from './Logo';
import AnimatedIcon from './AnimatedIcon';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import BuildIcon from '@mui/icons-material/Build';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExploreIcon from '@mui/icons-material/Explore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';

// Keyframes for animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Card style to match About/Contact
const aboutCardStyle = {
  background: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 4,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

// Helper for gradient text
const gradientText = {
  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const pricingOptions = [
  {
    title: 'Free Trial',
    price: '$0',
    description: '7-day trial',
    features: [
      'Personalized learning paths',
      'AI-powered recommendations',
      'Access to all content',
      'Community support',
    ],
    buttonText: 'Start Free Trial',
    buttonVariant: 'outlined',
  },
  {
    title: 'Monthly',
    price: '$4.99',
    description: '/ month',
    features: [
      'Personalized learning paths',
      'AI-powered recommendations',
      'Access to all content',
      'Community support',
      'Cancel anytime',
    ],
    buttonText: 'Subscribe Monthly',
    buttonVariant: 'contained',
  },
  {
    title: 'Yearly',
    price: '$50',
    description: '/ year',
    features: [
      'Personalized learning paths',
      'AI-powered recommendations',
      'Access to all content',
      'Community support',
      'Cancel anytime',
      '2 months free!',
    ],
    buttonText: 'Subscribe Yearly',
    buttonVariant: 'outlined',
    bestValue: true,
  },
];

const cardContentStyles = {
  textAlign: 'center',
  p: 0,
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
};

const gradeLevels = ['8th', '9th', '10th', '11th', '12th'];
const passions = ['Tech', 'Art', 'Sports', 'Business', 'Not sure yet'];

const LandingPage = ({ onGetStarted, onPricing }) => {
  const cardBaseStyle = {
    ...aboutCardStyle,
    border: '1.5px solid rgba(102,126,234,0.15)',
  };

  const popularCardStyle = {
    ...aboutCardStyle,
    border: '2.5px solid #FF8E53',
    position: 'relative',
    overflow: 'visible',
  };

  // Gradient button style
  const gradientButtonStyles = {
    fontWeight: 'bold',
    borderRadius: '8px',
    py: 1.5,
    color: '#fff',
    px: 0,
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 16px 0 rgba(102,126,234,0.15)',
    textTransform: 'none',
    fontSize: 18,
    letterSpacing: 1.2,
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'background 0.3s, box-shadow 0.3s',
    '&:hover': {
      background: 'linear-gradient(90deg, #764ba2 0%, #667eea 100%)',
      boxShadow: '0 6px 24px 0 rgba(102,126,234,0.25)'
    }
  };

  // Sheen animation for Best Value button
  const sheenKeyframes = `
    0% { left: -75%; }
    60% { left: 125%; }
    100% { left: 125%; }
  `;

  const bestValueButtonStyles = {
    ...gradientButtonStyles,
    '&:hover::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '50%',
      background: 'linear-gradient(120deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.05) 100%)',
      zIndex: 1,
      pointerEvents: 'none',
      animation: `${sheenKeyframes} 0.8s cubic-bezier(0.4,0,0.2,1)`
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-75%',
      height: '100%',
      width: '50%',
      background: 'linear-gradient(120deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.05) 100%)',
      zIndex: 1,
      pointerEvents: 'none',
      transition: 'left 0.3s',
    }
  };

  // Waitlist modal state
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [lockHovered, setLockHovered] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', grade_level: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleWaitlistOpen = () => setWaitlistOpen(true);
  const handleWaitlistClose = () => { setWaitlistOpen(false); setSuccess(false); setError(''); setForm({ name: '', email: '', grade_level: '' }); };
  const handleFormChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const API_URL = process.env.REACT_APP_API_URL || 'https://pathly-backend-tqce.onrender.com';
    try {
      const res = await fetch(`${API_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        let detail = 'Failed to join waitlist';
        try {
          const json = await res.json();
          detail = json.detail || detail;
        } catch {
          detail = res.statusText || detail;
        }
        throw new Error(detail);
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      height: '100vh',
      width: '100vw',
      overflowY: 'auto',
      scrollSnapType: 'y mandatory',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      {/* Hero Section - Full Screen */}
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 2, scrollSnapAlign: 'start' }}>
        <Logo />
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#fff', mt: 3, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
          Find Your Passion. Build Your Future.
        </Typography>
        <Typography variant="h5" sx={{ mb: 4, color: 'rgba(255,255,255,0.95)', maxWidth: 700, fontWeight: 400 }}>
          Start building a high school career that's uniquely you.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleWaitlistOpen}
            onMouseEnter={() => setLockHovered(true)}
            onMouseLeave={() => setLockHovered(false)}
            sx={{
              fontWeight: 'bold',
              borderRadius: '50px',
              px: 5,
              py: 1.5,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              fontSize: 20,
              boxShadow: '0 3px 12px 2px rgba(102,126,234,0.18)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              transition: 'background 0.3s, box-shadow 0.3s',
              '&:hover': {
                background: 'linear-gradient(90deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 6px 24px 0 rgba(102,126,234,0.25)'
              }
            }}
          >
            {lockHovered ? <LockIcon sx={{ fontSize: 28, transition: '0.2s' }} /> : <LockOpenIcon sx={{ fontSize: 28, transition: '0.2s' }} />}
            Lock In Early Access
          </Button>
          <Typography sx={{ color: '#ffe082', fontWeight: 600, fontSize: 16, mt: 1 }}>
            Only 150 early testers will get in.
          </Typography>
        </Box>
        {/* Waitlist Modal */}
        <Dialog open={waitlistOpen} onClose={handleWaitlistClose} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 900, fontSize: 28, textAlign: 'center', mt: 1 }}>
            You're Almost In 🎉
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
            <Typography sx={{ mb: 2, color: 'text.secondary' }}>
              Complete this quick form to secure your early access to Pathly.
            </Typography>
            {success ? (
              <Box sx={{ py: 4 }}>
                <Typography variant="h6" sx={{ color: 'success.main', mb: 2 }}>
                  🎊 You’re on the waitlist!
                </Typography>
                <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                  We’ll email you when your access is ready.
                </Typography>
                <Button onClick={handleWaitlistClose} variant="contained" color="primary">Close</Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleFormChange}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Grade Level"
                  name="grade_level"
                  value={form.grade_level}
                  onChange={handleFormChange}
                  select
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                >
                  {gradeLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </TextField>
                {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
                <DialogActions sx={{ flexDirection: 'column', gap: 1, mt: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      fontWeight: 700,
                      fontSize: 20,
                      borderRadius: 3,
                      py: 1.4,
                      mt: 1,
                      background: 'linear-gradient(90deg, #4fc3f7 0%, #1976d2 100%)',
                      color: '#fff',
                      boxShadow: '0 2px 8px #4fc3f755',
                      textTransform: 'uppercase',
                      letterSpacing: 1.2,
                      '&:hover': {
                        background: 'linear-gradient(90deg, #1976d2 0%, #4fc3f7 100%)',
                        color: '#fff',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : '✅ Claim My Spot'}
                  </Button>
                  <Typography sx={{ color: 'text.secondary', fontSize: 14, mt: 1 }}>
                    We’ll email you when your access is ready.
                  </Typography>
                </DialogActions>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </Box>
      {/* Maze Section - Full Screen, Centered, No Logo, Shorter Subtext, Icons in Row, Icon Shadow */}
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 2, scrollSnapAlign: 'start' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#fff', mt: 3, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
          Lost in the Extracurricular Maze?
        </Typography>
        <Typography variant="h5" sx={{ mb: 4, color: 'rgba(255,255,255,0.95)', maxWidth: 700, fontWeight: 400 }}>
          Students are pressured to impress, left guessing what colleges want, and waste time on the wrong activities.
        </Typography>
        {/* Maze Path Stepper - Icons in a straight row, circular soft shadow on icon */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: { xs: 2, md: 8 },
          mt: 2,
          mb: 0,
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
        }}>
          {[{
            icon: <DirectionsRunIcon fontSize="large" />, label: 'Pressure to impress', title: 'Pressure to impress', sub: 'The race to stand out leads to stress and anxiety.'
          }, {
            icon: <HelpOutlineIcon fontSize="large" />, label: 'Guessing what colleges want', title: 'Guessing what colleges want', sub: 'Uncertainty leads to wasted effort.'
          }, {
            icon: <HourglassEmptyIcon fontSize="large" />, label: 'Wasting time', title: 'Wasting time', sub: 'Valuable time is lost on what doesn\'t matter.'
          }, {
            icon: <ExploreIcon fontSize="large" />, label: 'Pathly shows the way', title: 'Pathly shows the way', sub: 'Pathly guides you to meaningful, authentic success.', highlight: true
          }].map((step, i) => (
            <Box key={step.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', flex: 1, minWidth: 0 }}>
              <Box sx={{ mb: 1, borderRadius: '50%', transition: 'box-shadow 0.2s, transform 0.2s', boxShadow: '0 4px 24px 0 rgba(110,126,234,0.18)', '&:hover': { boxShadow: '0 8px 32px 0 rgba(110,126,234,0.35)', transform: 'scale(1.13)' } }}>
                <AnimatedIcon icon={step.icon} label={step.label} highlight={step.highlight} />
              </Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>{step.title}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>{step.sub}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {/* Compass Section - Full Screen, 3-column grid, card pops out on hover, icon at top, no icon shadow */}
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 2, scrollSnapAlign: 'start' }}>
        <Container sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="h3" component="h2" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 4, color: '#fff' }}>
            Pathly is Your Compass
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.95)', mb: 6, maxWidth: 700, mx: 'auto' }}>
            We help you discover your passions, build a unique profile, and focus on what truly matters for college and beyond.
          </Typography>
          <Box sx={{
            display: { xs: 'flex', md: 'grid' },
            gridTemplateColumns: { md: 'repeat(3, 1fr)' },
            gap: 4,
            overflowX: { xs: 'auto', md: 'unset' },
            flexDirection: { xs: 'row', md: 'unset' },
            width: '100%',
            justifyContent: 'center',
            alignItems: 'stretch',
            pb: 2,
          }}>
            {[{
              icon: <EmojiObjectsIcon fontSize="large" />, title: 'Discover Interests', desc: 'Our AI gets to know you, suggesting activities and passion projects aligned with what you genuinely find exciting.', sub: 'No more guessing—find what you love and let it shine.'
            }, {
              icon: <BuildIcon fontSize="large" />, title: 'Build a Unique Profile', desc: 'We help you craft a compelling narrative that showcases your dedication and unique talents.', sub: 'Stand out to colleges with authenticity, not just a list of activities.'
            }, {
              icon: <TrackChangesIcon fontSize="large" />, title: 'Focus on Quality', desc: 'Forget the pressure to do everything. We guide you to focus on a few key areas where you can make a real impact.', sub: 'Quality over quantity—make your story matter.'
            }].map((card, i) => (
              <Box key={card.title} sx={{
                minWidth: { xs: 280, md: 0 },
                maxWidth: 400,
                flex: { xs: '0 0 80%', md: 'unset' },
                p: 4,
                bgcolor: '#fff',
                borderRadius: 4,
                textAlign: 'center',
                height: '100%',
                boxShadow: '0 4px 24px 0 rgba(110,126,234,0.10)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': { boxShadow: '0 8px 32px 0 rgba(110,126,234,0.25)', transform: 'scale(1.04)' },
                m: { xs: 2, md: 0 },
              }}>
                <Box sx={{ mb: 2 }}>
                  <AnimatedIcon icon={card.icon} label={card.title} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 2, color: '#6e7eea' }}>{card.title}</Typography>
                <Typography variant="body1" sx={{ color: '#333', mt: 1, mb: 1 }}>{card.desc}</Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>{card.sub}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage; 